import { getWeeklyPriceDisplay } from '../../utils/convertToWeeklyPrice';
import { computeGroupPerSeatCents } from '../utils/groupPricing';

import type { Price, Tier } from '../../types';

export interface PriceDisplayOptions {
  isPaidTrial: boolean;
  paidTrialPriceCents: number | null;
  paidTrialInlineSuffix: string | null;
  paidTrialAfterText: string | null;
  showDonationAmountInput: boolean;
  strikethroughCents: number | null;
  displayCents: number | null;
  isWeeklyDisplay: boolean;
  billedAsCents: number | null;
}

/**
 * Single source for which price figures a pricing surface shows, so the discount,
 * donation, paid-trial, weekly, and group cases can't drift between surfaces.
 */
export const getPriceDisplayOptions = ({
  selectedPrice,
  tier,
  isGroupMode = false,
  isInternalCheckout,
  showWeeklyPricing = false,
}: {
  selectedPrice?: Price;
  tier: Tier;
  isGroupMode?: boolean;
  isInternalCheckout: boolean;
  showWeeklyPricing?: boolean;
}): PriceDisplayOptions => {
  const { original_amount_cents, amount_cents, interval } = selectedPrice || {};
  const isDiscounted = !isGroupMode && amount_cents !== original_amount_cents && original_amount_cents;

  const isDonation = interval === 'donation';
  const showDonationAmountInput = isDonation && isInternalCheckout;

  const hasGroupDiscount =
    isGroupMode && !!tier.group_discount_percent && tier.group_discount_percent > 0 && !isDonation;

  const groupPerSeatCents =
    hasGroupDiscount && selectedPrice
      ? computeGroupPerSeatCents({
          amountCents: selectedPrice.original_amount_cents,
          discountPercent: tier.group_discount_percent,
        })
      : null;

  const cents = hasGroupDiscount ? groupPerSeatCents : isDiscounted ? amount_cents : original_amount_cents;

  const showDiscountedPrice = (isDiscounted || hasGroupDiscount) && !isDonation;

  const isPaidTrial = !isGroupMode && selectedPrice?.premium_offer?.offer_type === 'paid_trial';
  const paidTrialOffer = isPaidTrial ? selectedPrice?.premium_offer : undefined;

  const { weeklyCents, weeklyOriginalCents } = getWeeklyPriceDisplay({
    enabled: showWeeklyPricing,
    price: selectedPrice,
    displayCents: cents ?? undefined,
    isDiscounted: !!showDiscountedPrice,
    isPaidTrial,
  });
  const isWeeklyDisplay = weeklyCents != null;

  return {
    isPaidTrial,
    paidTrialPriceCents: paidTrialOffer?.paid_trial_price_cents ?? null,
    paidTrialInlineSuffix: paidTrialOffer?.paid_trial_inline_suffix ?? null,
    paidTrialAfterText: paidTrialOffer?.paid_trial_after_text ?? null,
    showDonationAmountInput,
    strikethroughCents:
      showDiscountedPrice && selectedPrice ? (weeklyOriginalCents ?? selectedPrice.original_amount_cents) : null,
    displayCents: weeklyCents ?? cents ?? null,
    isWeeklyDisplay,
    billedAsCents: isWeeklyDisplay ? (cents ?? null) : null,
  };
};
